import { Link } from '../link';
import styles from './brand.module.css';
import { LogoStrip } from './strip';

export { styles as brand };
export { Loader, LogoStrip, VersionStrip, DownloadStrip } from './strip';

/**
 * Brand primitives, from the guidelines.
 *
 * Kept apart from `ui.tsx`, which holds application chrome. These are the
 * pieces the guidelines actually specify — the mark, the runner, the stat, the
 * do/never pair, the flood band — and they carry the rules with them so a page
 * cannot get them subtly wrong.
 */

/**
 * The wordmark, with the strip above it.
 *
 * **The mark is the Progress Strip.** It was an elephant's head for a while and
 * is the strip again — so the device does two jobs, and the rule that keeps
 * that honest is section 03's rather than a separation of components: the
 * identity lockup draws `LOGO_BARS`, which are fixed, and every other strip in
 * the product draws numbers something measured. There is still no component
 * that takes arbitrary heights. See `./strip`.
 *
 * Two lockups: stacked by default, horizontal where there is no vertical room
 * (navigation bars, email headers). The wordmark is always solid — Ink on
 * light, Paper on dark — and never takes a channel colour. The top bar is the
 * panel's accent, which is the one place a colour is allowed into the lockup at
 * all.
 *
 * On the creators and developers sites the mark carries the site name after it.
 * The three sites share a ground per audience already, but colour alone is not
 * enough to tell a creator which domain they are on when they arrive from a
 * bookmark — and the guidelines forbid restyling the wordmark itself, so the
 * distinction is a separate word set beside it rather than a variant of the
 * mark.
 */
export function Logo({
  size = '1.5rem',
  horizontal = false,
  onDark = false,
  href = '/',
  display = false,
  bars = true,
}: {
  size?: string;
  horizontal?: boolean;
  onDark?: boolean;
  href?: string | null;
  /**
   * Whether the strip comes with the word.
   *
   * The lockup is the mark and the name together and that is what this draws by
   * default. False is the wordmark on its own — a variant, not a restyling:
   * the letters are still Fredoka at the brand's own width and weight, set by
   * this component rather than by whoever wanted the name on a page. It exists
   * so that wanting the name alone does not mean typing `GOOD BUILD` into a
   * `<span>` somewhere and picking the tracking by eye.
   */
  bars?: boolean;
  /**
   * The mark as the title of a page rather than as furniture on it.
   *
   * The strip scales with the word instead of stepping through the four fixed
   * sizes, which is what a `clamp()`ed title needs — see `.stripDisplay`. The
   * font size moves to the lockup so both halves can measure against it.
   */
  display?: boolean;
}): React.ReactElement {
  const word = (
    <span className={styles.wordLockup}>
      <span className={styles.word} {...(display ? {} : { style: { fontSize: size } })}>
        Good Build
      </span>
    </span>
  );

  /*
    The strip's size is a step of its own scale rather than a multiple of the
    wordmark's, because the bars are rectangles on a fixed grid — `.stripTiny`
    and `.stripSm` in the stylesheet set a height, a bar width and a gap that
    stay in proportion to each other, and a strip scaled off a font size would
    land between two of them with sub-pixel gaps.

    Four bars beside the word, five above it. That is the guidelines' own rule
    for small sizes: the horizontal lockup is used at navigation-bar height,
    where the first two bars are a few pixels apart and the fifth is the only
    one anybody reads.
  */
  const content = !bars ? (
    word
  ) : horizontal ? (
    <>
      <LogoStrip size="tiny" bars={4} onDark={onDark} />
      {word}
    </>
  ) : (
    <>
      <LogoStrip size={display ? 'display' : 'sm'} onDark={onDark} />
      {word}
    </>
  );

  const className = [
    horizontal ? styles.logoRow : styles.logo,
    display ? styles.logoDisplay : '',
    onDark ? styles.logoOnDark : '',
  ]
    .filter(Boolean)
    .join(' ');

  const sized = display ? { style: { fontSize: size } } : {};

  if (href === null)
    return (
      <span className={className} {...sized}>
        {content}
      </span>
    );
  return (
    <Link href={href} className={className} {...sized} aria-label="GOOD BUILD home">
      {content}
    </Link>
  );
}

/**
 * The wave, lifted from the brand guidelines.
 *
 * `goodbuild-brand-guidelines.html` carries it as `.wave`: a single bezier on a
 * 1200 by 60 box, hung on the bottom edge of a coloured section and filled with
 * whatever the section is sitting on — so the colour above it ends in a wave
 * rather than in a straight rule.
 *
 * The path is that file's, character for character. It is the one curve in a
 * brand made of rectangles and it earns that by being rare; redrawing it by eye
 * would be inventing a second wave that nearly matches the first.
 *
 * **`preserveAspectRatio="none"` is what makes it a device rather than a
 * picture.** The box stretches to whatever width it is given and the curve
 * stretches with it, so the crest count is the same on a phone and on a
 * thirty-inch monitor. Scaled proportionally instead, a wide window would show
 * one long swell and the shape would stop being recognisable.
 *
 * It fills with `currentColor`, so the caller sets `color` to whatever is
 * *below* the wave and the element's own background to whatever is above it.
 */
export function Wave({ className }: { className?: string }): React.ReactElement {
  return (
    <svg
      className={className}
      viewBox="0 0 1200 60"
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="currentColor"
        d="M0,28 C150,58 300,2 450,24 C600,46 750,8 900,28 C1020,44 1120,18 1200,30 L1200,60 L0,60 Z"
      />
    </svg>
  );
}

/** A section label above a heading. Martian Mono, wide tracking. */
export function Eyebrow({ children }: { children: React.ReactNode }): React.ReactElement {
  return <p className="gb-eyebrow">{children}</p>;
}

/** The running header from the guidelines' page furniture. */
export function Runner({ left, right }: { left: string; right?: string }): React.ReactElement {
  return (
    <div className={styles.runner}>
      <span>{left}</span>
      <span>{right ?? 'GOOD BUILD'}</span>
    </div>
  );
}

/**
 * A measurement and what it measures.
 *
 * `label` is required and carries the denominator. Section 06: *"86% positive"
 * is marketing; "86% from 74 reviews on v2.6" is information.* There is no
 * variant of this component that renders a bare figure, and unflattering
 * numbers get the same type, size and weight as flattering ones — never a
 * smaller font, never a muted grey.
 *
 * The figure is the panel's accent and there is no way to ask for another
 * colour. A `channel` prop used to choose between the four, which is how a
 * single row of statistics ended up carrying three signal colours; a panel now
 * has exactly one, so there is nothing left to choose.
 */
export function Stat({ value, label }: { value: string; label: string }): React.ReactElement {
  return (
    <div className={styles.stat}>
      <span className={styles.statValue}>{value}</span>
      <span className={styles.statLabel}>{label}</span>
    </div>
  );
}

/** A do / never pair. Build rules the first, Hot the second. */
export function Do({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className={`${styles.dd} ${styles.ddYes}`}>
      <p className={styles.ddTitle}>{title}</p>
      {children}
    </div>
  );
}

export function Never({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className={`${styles.dd} ${styles.ddNo}`}>
      <p className={styles.ddTitle}>{title}</p>
      {children}
    </div>
  );
}

/**
 * A full-bleed colour field.
 *
 * The guidelines' flood pages, translated to a scrolling document. The band
 * redefines the surface-role tokens rather than only setting a colour, so type
 * and rules inside it invert correctly without every child needing to know it
 * is on Ink.
 */
export function Band({
  tone,
  children,
}: {
  /**
   * `accent` floods with the panel's own colour — Build on the store, Hot on
   * creators, Solar on a working surface. It was `build`, which named a channel
   * and so put a blue field on panels that do not have blue in their three.
   */
  tone: 'ink' | 'accent';
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <section
      className={
        tone === 'ink'
          ? `${styles.band} ${styles.bandInk} gb-dotted`
          : `${styles.band} ${styles.bandAccent}`
      }
      data-gb-surface={tone === 'ink' ? 'ink' : undefined}
    >
      <div className={styles.bandInner}>{children}</div>
    </section>
  );
}

/**
 * The panel's own bar. A terminator, used once per page.
 *
 * Four segments alternating between the panel's accent and its type colour.
 * This was the four channel colours side by side, which meant every page
 * carried every signal colour in the system regardless of whose panel it was.
 */
export function PanelBar(): React.ReactElement {
  return (
    <div className={styles.channelBar} aria-hidden="true">
      <i />
      <i />
      <i />
      <i />
    </div>
  );
}

/**
 * A call to action.
 *
 * An absolute `href` renders an anchor rather than a `next/link`, because the
 * three sites are separate origins and a client-side navigation cannot cross
 * one. This matters more here than anywhere: the calls to action are exactly
 * the links that send a creator or a studio from the store to their own site.
 *
 * ## A destination that does not exist yet
 *
 * `href` takes null, and a null one renders the button as a real button, marked
 * unavailable, rather than as nothing.
 *
 * A join button read out of `DISCORD_INVITE` is what this was built for. An
 * environment without one used to render no button at all — which is right
 * about the link and wrong about the page: the ask is part of the shape of a
 * layout, and a screen missing its action reads as broken rather than as
 * unconfigured. So the button stays and stops being a link.
 *
 * **It is a `<button disabled>`, never an anchor with a dead href.** That is the
 * whole point of the state. A plausible link that 404s is the failure the rule
 * behind this was written against — the guidelines' section 29.2 — and this does
 * not reintroduce it: there is nothing to click, the pointer says so, a screen
 * reader says so, and the press animation in `tokens.css` already excludes a
 * disabled button.
 *
 * It is a *pending* state rather than a general disabled one, and the difference
 * is worth keeping: this is a control whose destination is not configured yet,
 * not a control the reader is not allowed to use. Nothing here is ever enabled
 * by something a visitor does.
 */
export function Cta({
  href,
  children,
  variant = 'primary',
  size = 'default',
}: {
  /**
   * Where it goes. Null draws the button as unavailable rather than drawing
   * nothing — see above, and never pass a placeholder URL instead.
   */
  href: string | null;
  children: React.ReactNode;
  /**
   * Filled in the panel's accent, or outlined and filling on hover.
   *
   * There was a third, `hot`, which painted a button Hot on any panel. With one
   * accent per panel it had nothing left to mean: on the creators site it was
   * the accent already, and anywhere else it was a colour that panel does not
   * own.
   */
  variant?: 'primary' | 'ghost';
  /**
   * How loud it is, which is a different question from what it looks like.
   *
   * `lead` is for the one action a page exists to ask for. It exists because a
   * page once spent a full screen arguing somebody should do something and then
   * asked them to with a fourteen-pixel button, the same size as the "read
   * more" link beside it. A page whose primary action is set at the size of its
   * secondary one has two secondary actions.
   *
   * It scales the type and the box together and changes nothing else — same
   * face, same tracking, same fill, same hover. Reach for it once per page or
   * it is not leading anything.
   */
  size?: 'default' | 'lead';
}): React.ReactElement {
  const className = [
    styles.cta,
    href === null ? styles.ctaPending : variant === 'ghost' ? styles.ctaGhost : styles.ctaPrimary,
    size === 'lead' ? styles.ctaLead : '',
  ]
    .filter(Boolean)
    .join(' ');

  /*
   * No destination, so no link. `type="button"` because an unconfigured call to
   * action inside a form would otherwise submit it, which is the one way a
   * button that does nothing can still do something.
   */
  if (href === null) {
    return (
      <button type="button" className={className} disabled>
        {children}
      </button>
    );
  }

  if (href.startsWith('http://') || href.startsWith('https://')) {
    return (
      <a href={href} className={className}>
        {children}
      </a>
    );
  }

  return (
    <Link href={href} className={className}>
      {children}
    </Link>
  );
}
